'use client'

import { getPlatformName } from '@/actions/platform'
import AdBannerWrapper from '@/components/_cards/ad/ad-banner-wrapper'
import MobileAdBannerWrapper from '@/components/_cards/ad/mobile-ad-banner-wrapper'
import DonationCampaignCta from '@/components/_registration/donation-campaign-cta'
import LoginButton from '@/components/_registration/login-button'
import ImageWithFallback from '@/components/common/image-with-fallback'
import ControllerIcon from '@/components/icons/controller-icon'
import HelmetIcon from '@/components/icons/helmet-icon'
import MedalIcon from '@/components/icons/medal'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getDonationCampaign } from '@/lib/donation-campaigns'
import {
  canRecruitForChampionship,
  getEffectiveRecruitmentOpenDateTime,
} from '@/lib/recruitment-scheduling'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import ChampionshipAttendeeStatusButton from './championship-attendee-status-button'
import FrontEndBreadcrumbs from './front-end-breadcrumbs'
import ScheduledPreviewBanner from './scheduled-preview-banner'
import TabsSection from './tabs-section'

function useRegistrationScheduled(championship) {
  const [now, setNow] = useState(null)
  const registrationOpenDateTime =
    getEffectiveRecruitmentOpenDateTime(championship)
  const registrationOpenTimestamp = registrationOpenDateTime
    ? new Date(registrationOpenDateTime).getTime()
    : null
  const hasValidRegistrationOpenTimestamp = Number.isFinite(
    registrationOpenTimestamp,
  )

  useEffect(() => {
    if (!hasValidRegistrationOpenTimestamp) return

    const currentNow = Date.now()
    setNow(currentNow)

    if (registrationOpenTimestamp <= currentNow) return

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now()
      setNow(nextNow)

      if (registrationOpenTimestamp <= nextNow) {
        window.clearInterval(intervalId)
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [hasValidRegistrationOpenTimestamp, registrationOpenTimestamp])

  if (!hasValidRegistrationOpenTimestamp || !now) return false

  return registrationOpenTimestamp > now
}

export default function ChampionshipPage({
  championship,
  events,
  championshipAttendees,
  params,
  searchParams,
  session,
  bannerAd,
  mobileBannerAd,
}) {
  return (
    <>
      <ChampionshipHeader
        {...{
          championship,
          session,
          params,
          championshipAttendees,
          championshipAttendeesLoading: false,
          mutateChampionshipAttendees: null,
        }}
      />

      <ScheduledPreviewBanner championship={championship} />

      {/* Banner Ad */}
      {bannerAd && (
        <div className="mt-6 desktop-xs:mt-8">
          <AdBannerWrapper
            ad={bannerAd}
            placement="championship-page-banner"
            index={0}
          />
        </div>
      )}

      {/* Mobile Banner Ad */}
      {mobileBannerAd && (
        <div className="mt-6 desktop-xs:mt-8">
          <MobileAdBannerWrapper
            ad={mobileBannerAd}
            placement="championship-page-mobile-banner"
            index={0}
          />
        </div>
      )}

      <TabsSection
        {...{
          searchParams,
          championship,
          events,
          championshipAttendees,
          championshipAttendeesLoading: false,
        }}
      />
    </>
  )
}

function ChampionshipHeader({
  championship,
  session,
  params,
  championshipAttendees,
  championshipAttendeesLoading,
  mutateChampionshipAttendees,
}) {
  const platformDisplayName = getPlatformName(championship.platforms)
  const registrationScheduled = useRegistrationScheduled(championship)
  const registrationOpen = canRecruitForChampionship(championship)
  const donationCampaign = getDonationCampaign({
    championshipId: championship.id,
  })
  const [donationDialogOpen, setDonationDialogOpen] = useState(false)
  const handleRegistrationSuccess = useCallback(() => {
    if (donationCampaign?.autoOpenAfterRegistration) {
      setDonationDialogOpen(true)
    }
  }, [donationCampaign])

  return (
    <div className="w-full font-inter">
      <div className="divide h-px bg-neutral-blue-700" />

      <div className="screen-grid *:grid-indent h-[53px] desktop-xs:h-[67px]">
        <FrontEndBreadcrumbs />
      </div>

      <div className="divide h-px bg-neutral-blue-600 desktop-xs:hidden" />

      <div className="screen-grid rounded-b-[20px]! bg-linear-to-t relative overflow-hidden from-neutral-blue-800 to-neutral-blue-900 pb-[26px] pt-7 desktop-xs:pb-[72px] desktop-xs:pt-[50px]">
        <div className="col-span-full flex h-fit flex-col flex-wrap gap-3 desktop-xs:col-span-5 desktop-xs:gap-5 desktop-xs:gap-y-3 desktop-sm:col-span-6 desktop-sm:col-start-2">
          <h2 className="text-h-4 font-semibold desktop-xs:text-h-1 desktop-xs:font-bold">
            {championship.name}
          </h2>
          <div className="flex h-fit w-fit items-center gap-2 rounded-full bg-[#EEF2FF] px-4 py-2">
            <MedalIcon className="size-4 stroke-[#484BD0] stroke-2 desktop-xs:size-5" />
            <p className="text-p-1 font-bold text-[#484BD0] desktop-xs:font-semibold">
              Championship
            </p>
          </div>
        </div>

        <div className="col-span-full mt-3 flex items-center desktop-xs:mt-6 desktop-sm:col-span-6 desktop-sm:col-start-2">
          <div className="relative size-7 overflow-hidden rounded-md bg-neutral-blue-100/20 desktop-xs:size-10">
            <ImageWithFallback
              alt=""
              className="object-cover"
              src={championship.community.logoImgUrl}
              fallbackUrl="/img/default/header.webp"
              fill
            />
          </div>
          <span className="ml-2 text-p-1 font-semibold text-neutral-blue-200">
            Hosted by&nbsp;
          </span>
          <Link
            href={`/c/${params.communitySlug}`}
            className="inline-block text-p-1 font-semibold text-shades-0 underline underline-offset-2"
          >
            {championship.community.name}
          </Link>
          <div className="mx-6 hidden h-full w-px bg-neutral-blue-500 desktop-xs:block" />
          <ControllerIcon className="hidden size-7 stroke-white desktop-xs:block" />
          <div className="ml-2 hidden text-p-1 font-semibold desktop-xs:block">
            {championship.game.nicename}
            {championship.platforms.length > 0 && (
              <>
                {' - '}
                {platformDisplayName !== 'Multi-Platform' ? (
                  platformDisplayName
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className="w-fit cursor-pointer">
                          Multi-Platform ({championship.platforms.length})
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="max-h-[300px] overflow-y-auto">
                          {championship.platforms.map((platform) => (
                            <div
                              key={`platform-${platform.platform.name}`}
                              className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                            >
                              {platform.platform.nicename}
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            )}
          </div>
        </div>

        <div className="col-span-full mt-5 flex h-fit items-center desktop-xs:hidden desktop-sm:col-span-6 desktop-sm:col-start-2">
          <ControllerIcon className="size-7 stroke-white" />
          <p className="ml-2 text-p-1 font-semibold">
            {championship.game.nicename}
            {championship.platforms.length > 0 && (
              <>
                {' - '}
                {platformDisplayName !== 'Multi-Platform' ? (
                  platformDisplayName
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className="w-fit cursor-pointer">
                          Multi-Platform ({championship.platforms.length})
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="max-h-[300px] overflow-y-auto">
                          {championship.platforms.map((platform) => (
                            <div
                              key={`platform-${platform.platform.name}`}
                              className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                            >
                              {platform.platform.nicename}
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            )}
          </p>
        </div>

        <div
          className={`fixed bottom-0 z-10 col-span-full -ml-6 mt-5 flex h-fit w-full flex-col items-center px-6 pt-8 desktop-xs:relative desktop-xs:z-0 desktop-xs:col-span-5 desktop-xs:ml-0 ${championship.splits.length > 0 ? 'mt-7' : 'desktop-xs:mt-[60px]'} desktop-xs:flex-row desktop-xs:bg-transparent desktop-xs:px-0 desktop-xs:pt-0 desktop-sm:col-span-6 desktop-sm:col-start-2`}
        >
          <DonationCampaignCta
            campaign={donationCampaign}
            className="desktop-xs:max-w-[328px]"
            open={donationDialogOpen}
            onOpenChange={setDonationDialogOpen}
          >
            {session ? (
              <ChampionshipAttendeeStatusButton
                session={session}
                championship={championship}
                mutateDriverCount={mutateChampionshipAttendees}
                onRegistrationSuccess={handleRegistrationSuccess}
              />
            ) : (
              <LoginButton className="z-20 lg:z-0 desktop-xs:w-[158px]" />
            )}
          </DonationCampaignCta>

          <div className="z-11 flex w-full pb-[30px] pt-3 desktop-xs:w-fit desktop-xs:pb-0 desktop-xs:pt-0">
            <div className="mx-auto flex items-center text-nowrap">
              <HelmetIcon className="ml-8 hidden size-5 stroke-white lg:inline-block" />

              <p
                className={`ml-2 hidden text-end text-p-1 lg:inline-block ${registrationOpen ? 'text-success-500' : 'text-error-500'} desktop-xs:text-p-2`}
              >
                •
              </p>

              <div
                className={`ml-2 text-end text-p-1 ${registrationOpen ? 'text-success-500' : 'text-error-500'} desktop-xs:text-p-2`}
              >
                {registrationOpen ? (
                  <span className="flex items-center gap-1">
                    Open:{' '}
                    {championshipAttendeesLoading ? (
                      <Skeleton className="inline-block h-[20px] w-[100px]" />
                    ) : (
                      <span>
                        {championshipAttendees?.length ?? 0} Registered
                      </span>
                    )}
                  </span>
                ) : registrationScheduled ? (
                  'Scheduled'
                ) : (
                  'Closed'
                )}
              </div>
            </div>
          </div>

          <div className="bg-linear-to-b absolute bottom-0 left-0 right-0 top-0 from-transparent via-neutral-blue-900 via-25% to-neutral-blue-900 desktop-xs:hidden" />
        </div>

        {championship.headerImgUrl && (
          <div className="relative col-span-3 col-start-6 hidden w-full desktop-xs:block desktop-sm:col-span-4 desktop-sm:col-start-8 desktop-sm:row-span-4 desktop-sm:row-start-1">
            <ImageWithFallback
              alt=""
              className="bg-neutral-blue-100/20bg-neutral-blue-100/20 h-auto! mt-auto aspect-video rounded-md object-cover"
              src={championship.headerImgUrl}
              fallbackUrl="/img/default/header.webp"
              fill
            />
          </div>
        )}
      </div>
    </div>
  )
}
